import {
  type FC, useCallback, useEffect,
} from 'react';
import { useDispatch } from 'react-redux';
import { useConfirmationModal } from '~/_shared/components/modal/confirmation/useConfirmationModal';
import { useCreateNewMap } from '~/_shared/components/prepareDataModal/useCreateNewMap';
import { CONFIG } from '~/_shared/constants/config';
import { type MapPrivacyLevel } from '~/_shared/types/map';
import { useTranslation } from '~/_shared/utils/hooks';
import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { notNullsy } from '~/_shared/utils/typeGuards';
import type { MapPrivacyClickParams } from '~/map/listing/tableData/mapRow/mapRow.component';
import { ModalType } from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import { mapReset } from '~/store/map/map.actionCreators';
import { openModal } from '~/store/modal/modal.actionCreators';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { useUserCurrentClientSelector } from '~/store/userData/userData.selectors';
import { MapListingDeleteStatus } from './actionsHeader/actionsHeader.component';
import { MapListingComponent } from './mapListing.component';
import { useMapDropDown } from './useMapDropDown.hook';
import { useMapListingDeleteWithStatus } from './useMapListingDeleteWithStatus';
import { useMapListingToggleManagement } from './useMapListingToggleManagement.hook';

export const MapListingContainer: FC = () => {
  const clientId = useClientIdSelector();
  const { sortType, setSortType } = useMapDropDown(clientId);
  const { openModal: openMapPrivacySelectorModal } = useModal(ModalType.MapPrivacySelector);
  const {
    onToggleMapSelect, onToggleSubMapSelect, selectedMapIds, selectedMaps, selectedSubMapIds, onToggleShowSubMaps,
    mapListingItems, pagination, onPageChange, isLoading: isMapListingDataLoading, errorMessage, toggledMapIds,
    setSelectedMapIds, setSelectedSubMapIds, forceRefresh: forceRefreshListingData, searchQuery,
    onSearchQueryChange, changePrivacyLevel, allMapListingItems,
  } = useMapListingToggleManagement(sortType);
  const currentMapId = useMapIdSelector();
  const dispatch = useDispatch();
  const { openConfirmationModal, closeConfirmationModal } = useConfirmationModal();
  const [t] = useTranslation();
  const currentClient = useUserCurrentClientSelector();
  const { onCreateNewMap } = useCreateNewMap();
  const migrationFeatureEnabled = CONFIG.ENABLE_MIGRATION;
  const hasMapsToMigrate = useSelector(state => state.mapMigration.v4MapInfos.length !== 0);
  const canUserInitiateMapMigration = migrationFeatureEnabled && (currentClient?.migrationDone === false) && hasMapsToMigrate;

  const onMigrationButtonClick = useCallback(() => dispatch(openModal(ModalType.MigrateMaps, {})), [dispatch]);

  const onDeleteSuccess = () => {
    setSelectedMapIds([]);
    setSelectedSubMapIds(new Set());
    forceRefreshListingData();

    const currentMapDeleted = currentMapId !== null
        && (selectedMaps.map((map) => map.id === currentMapId) || selectedSubMapIds.has(currentMapId));

    if (currentMapDeleted) {
      dispatch(mapReset());
    }
  };

  const { status, resetStatus, onDelete } = useMapListingDeleteWithStatus({
    selectedMaps, selectedMapIds, selectedSubMapIds, onSuccess: onDeleteSuccess, allMapListingItems,
  });

  const handleMapDelete = useCallback(() => {
    openConfirmationModal({
      confirmCaption: t('Proceed'),
      isConfirmButtonDestructive: true,
      onCancel: closeConfirmationModal,
      onConfirm: () => {
        onDelete();
        closeConfirmationModal();
      },
      title: t('Delete Maps'),
      text: t('MapsDeletionConfirmation'),
    });
  }, [closeConfirmationModal, onDelete, openConfirmationModal, t]);

  const onMapPrivacyClick = useCallback(({
    isBaseLayer, mapId, mapName, mapSource, parentMapId, parentMapPrivacy, privacyLevel,
  }: MapPrivacyClickParams) => {
    const isSnapshot = notNullsy(parentMapId) && !isBaseLayer;
    const onSuccess = (newPrivacyLevel: MapPrivacyLevel) => {
      changePrivacyLevel({
        isBaseLayer,
        mapId,
        newPrivacyLevel,
        parentMapId,
      });
    };

    openMapPrivacySelectorModal({
      isSnapshot,
      mapName,
      mapPrivacyInfo: {
        mapId,
        mapPrivacyLevel: privacyLevel,
        parentMapId,
        parentMapPrivacy,
        mapSource,
      },
      onSuccess,
    });
  }, [changePrivacyLevel, openMapPrivacySelectorModal]);

  useEffect(() => {
    setSelectedMapIds([]);
    setSelectedSubMapIds(new Set());
  }, [pagination?.currentPage, setSelectedMapIds, setSelectedSubMapIds]);

  return (
    <MapListingComponent
      deleteStatus={status}
      errorMessage={errorMessage}
      isMapListingDataLoading={isMapListingDataLoading || status === MapListingDeleteStatus.Loading}
      onDelete={handleMapDelete}
      onDeleteSuccessAnimationFinish={resetStatus}
      onMapPrivacyClick={onMapPrivacyClick}
      onMigrationButtonClick={canUserInitiateMapMigration ? onMigrationButtonClick : undefined}
      onCreateNewMap={onCreateNewMap}
      onPageChange={onPageChange}
      onSearchQueryChange={onSearchQueryChange}
      onSortTypeChange={setSortType}
      pagination={pagination}
      processedItems={mapListingItems}
      searchQuery={searchQuery}
      selectedMapIds={selectedMapIds}
      selectedSubMapIds={selectedSubMapIds}
      sortType={sortType}
      toggleMapSelection={onToggleMapSelect}
      toggleSubMapSelection={onToggleSubMapSelect}
      toggleSubMapVisibility={onToggleShowSubMaps}
      toggledMapIds={toggledMapIds}
      onShareSuccess={changePrivacyLevel}
    />
  );
};
